import { useState, useEffect, useRef, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { computeSeoScore, scoreColor } from "../utils/seoScore";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";

// Isolated copywriter cabinet, two levels:
//   1) projects — pick/claim a project ("Взяти проєкт", hard lock)
//   2) texts of the taken project → Word-like editor (SunEditor) + live SEO score.

const STATUS_LABEL = {
  assigned: { txt: "Нове ТЗ", color: "#6366f1" },
  in_progress: { txt: "В роботі", color: "#f59e0b" },
  submitted: { txt: "На перевірці", color: "#0ea5e9" },
  revisions: { txt: "Правки", color: "#ef4444" },
  accepted: { txt: "Прийнято", color: "#16a34a" },
};

const pathOf = (u) => { try { return new URL(u).pathname || u; } catch { return u; } };
const htmlToText = (html) => { const d = document.createElement("div"); d.innerHTML = html || ""; return d.innerText || d.textContent || ""; };

const EDITOR_BUTTONS = [
  ["undo", "redo"],
  ["formatBlock", "fontSize"],
  ["bold", "underline", "italic", "strike", "subscript", "superscript"],
  ["fontColor", "hiliteColor", "removeFormat"],
  ["align", "list", "indent", "outdent", "lineHeight"],
  ["table", "link", "image", "horizontalRule"],
  ["fullScreen", "codeView"],
];

export default function CopywriterView() {
  const { apiFetch, dark, flash } = useApp();
  const [level, setLevel] = useState("projects");     // 'projects' | 'texts'
  const [projects, setProjects] = useState([]);
  const [loadingP, setLoadingP] = useState(true);
  const [curProject, setCurProject] = useState(null); // { project_id, project_name, ... }
  const [texts, setTexts] = useState([]);
  const [sel, setSel] = useState(null);               // open item from /item/:id
  const [score, setScore] = useState(null);
  const [saving, setSaving] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const htmlRef = useRef("");
  const saveTimer = useRef(null);
  const scoreTimer = useRef(null);

  const c = {
    panel: dark ? "#17171f" : "#fff",
    border: dark ? "#2a2a35" : "#e5e7eb",
    text: dark ? "#e4e4ec" : "#1a1a2e",
    muted: dark ? "#8b8b9a" : "#6b7280",
  };

  // ── Level 1: projects ──
  const loadProjects = useCallback(() => {
    setLoadingP(true);
    apiFetch("/api/copywriter/projects").then(r => r.json()).then(d => {
      setProjects(d.projects || []); setLoadingP(false);
    }).catch(() => setLoadingP(false));
  }, [apiFetch]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const openProject = (p) => {
    setCurProject(p); setSel(null); setLevel("texts");
    apiFetch(`/api/copywriter/project/${p.project_id}/texts`).then(r => r.json()).then(d => setTexts(d.items || [])).catch(() => setTexts([]));
  };

  const takeProject = (p, e) => {
    e.stopPropagation();
    apiFetch(`/api/copywriter/project/${p.project_id}/take`, { method: "POST" })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "err"); return d; })
      .then(() => { flash("Проєкт узято ✓"); openProject({ ...p, owner_id: -1 }); loadProjects(); })
      .catch(err => flash(err.message));
  };

  const releaseProject = () => {
    if (!curProject) return;
    if (!window.confirm("Звільнити проєкт? Незавершені тексти повернуться у пул для інших копірайтерів.")) return;
    apiFetch(`/api/copywriter/project/${curProject.project_id}/release`, { method: "POST" })
      .then(() => { flash("Проєкт звільнено"); setLevel("projects"); setCurProject(null); loadProjects(); })
      .catch(err => flash(err.message));
  };

  const reloadTexts = () => {
    if (!curProject) return;
    apiFetch(`/api/copywriter/project/${curProject.project_id}/texts`).then(r => r.json()).then(d => setTexts(d.items || [])).catch(() => {});
  };

  // ── Level 2: a text ──
  const openItem = (id) => {
    apiFetch(`/api/copywriter/item/${id}`).then(r => r.json()).then(d => {
      htmlRef.current = d.html_content || "";
      setSel(d);
      const s = computeSeoScore({ html: d.html_content || "", plainText: htmlToText(d.html_content), keywords: (d.brief && d.brief.keywords) || [], ngrams: (d.brief && d.brief.ngrams) || [], recommendedLength: (d.brief && d.brief.recommendedLength) || { min: 1500, max: 4000 } });
      setScore(s);
    }).catch(e => flash("Помилка: " + e.message));
  };

  const brief = sel?.brief || {};
  const recLen = brief.recommendedLength || { min: 1500, max: 4000 };

  const recalc = (html) => {
    const s = computeSeoScore({ html, plainText: htmlToText(html), keywords: brief.keywords || [], ngrams: brief.ngrams || [], recommendedLength: recLen });
    setScore(s);
  };

  const onEditorChange = (content) => {
    htmlRef.current = content;
    if (scoreTimer.current) clearTimeout(scoreTimer.current);
    scoreTimer.current = setTimeout(() => recalc(content), 400);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaving("saving");
    saveTimer.current = setTimeout(saveDraft, 1500);
  };

  const saveDraft = () => {
    if (!sel) return;
    const html = htmlRef.current || "";
    const plain = htmlToText(html);
    const s = computeSeoScore({ html, plainText: plain, keywords: brief.keywords || [], ngrams: brief.ngrams || [], recommendedLength: recLen });
    apiFetch(`/api/copywriter/item/${sel.id}`, { method: "PUT", body: JSON.stringify({ html_content: html, plain_text: plain, seo_score: s || {} }) })
      .then(r => r.json()).then(d => {
        setSaving("saved");
        if (d.writer_status && d.writer_status !== sel.writer_status) setSel(prev => ({ ...prev, writer_status: d.writer_status }));
        setTimeout(() => setSaving(""), 1500);
      }).catch(() => setSaving(""));
  };

  const submit = () => {
    if (!sel) return;
    saveDraft();
    setSubmitting(true);
    setTimeout(() => {
      apiFetch(`/api/copywriter/item/${sel.id}/submit`, { method: "POST" })
        .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || "err"); return d; })
        .then(() => { flash("Передано редактору на перевірку ✓"); setSel(null); reloadTexts(); })
        .catch(e => flash("Помилка: " + e.message))
        .finally(() => setSubmitting(false));
    }, 300);
  };

  const kwGroups = [
    { label: "ВЧ (>1000)", keys: (brief.keywords || []).filter(k => (k.frequency || 0) > 1000) },
    { label: "СЧ (100–1000)", keys: (brief.keywords || []).filter(k => (k.frequency || 0) >= 100 && (k.frequency || 0) <= 1000) },
    { label: "НЧ (10–100)", keys: (brief.keywords || []).filter(k => (k.frequency || 0) >= 10 && (k.frequency || 0) < 100) },
    { label: "Мікро (<10)", keys: (brief.keywords || []).filter(k => (k.frequency || 0) >= 1 && (k.frequency || 0) < 10) },
  ].filter(g => g.keys.length);

  // ── Render: Level 1 (projects) ──
  if (level === "projects") {
    return (
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: c.muted, marginBottom: 12, textTransform: "uppercase", letterSpacing: ".5px" }}>Проєкти · {projects.length}</div>
        {loadingP ? <div style={{ color: c.muted }}>Завантаження…</div> :
          projects.length === 0 ? <div style={{ color: c.muted, padding: "30px 0" }}>Поки немає проєктів у роботі.</div> :
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {projects.map(p => {
              const free = p.owner_id == null;
              const mine = p.my_count > 0;
              const otherOwned = !free && !mine;
              return (
                <div key={p.project_id} onClick={() => mine && openProject(p)}
                  style={{ background: c.panel, border: `1px solid ${mine ? "#16a34a" : c.border}`, borderRadius: 10, padding: 16, cursor: mine ? "pointer" : "default", opacity: otherOwned ? 0.6 : 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: c.text, marginBottom: 4 }}>{p.project_name}</div>
                  <div style={{ fontSize: 12, color: c.muted, marginBottom: 12 }}>{p.active_count} текстів{p.nearest_deadline ? ` · ⏱ ${String(p.nearest_deadline).slice(0, 10)}` : ""}</div>
                  {free && <button onClick={(e) => takeProject(p, e)} style={{ width: "100%", background: "linear-gradient(135deg,#16a34a,#22c55e)", border: "none", borderRadius: 8, padding: "8px", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer" }}>Взяти проєкт</button>}
                  {mine && <div style={{ fontSize: 12, fontWeight: 700, color: "#16a34a" }}>✓ Ваш проєкт · {p.my_count} у роботі → відкрити</div>}
                  {otherOwned && <div style={{ fontSize: 12, fontWeight: 700, color: c.muted }}>🔒 Зайнято: {p.owner_name || "інший копірайтер"}</div>}
                </div>
              );
            })}
          </div>}
      </div>
    );
  }

  // ── Render: Level 2 (texts of a project) ──
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <button onClick={() => { setLevel("projects"); setSel(null); loadProjects(); }} style={{ background: dark ? "#22222c" : "#f3f4f6", border: `1px solid ${c.border}`, borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer", color: c.text }}>← Проєкти</button>
        <span style={{ fontSize: 15, fontWeight: 700, color: c.text }}>{curProject?.project_name}</span>
        <button onClick={releaseProject} style={{ marginLeft: "auto", background: "none", border: `1px solid ${c.border}`, borderRadius: 6, padding: "5px 10px", fontSize: 11, cursor: "pointer", color: c.muted }}>Звільнити проєкт</button>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        {/* Texts queue */}
        <div style={{ width: 260, flexShrink: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: c.muted, marginBottom: 8 }}>Тексти · {texts.length}</div>
          {texts.length === 0 ? <div style={{ color: c.muted, fontSize: 13 }}>Немає активних текстів.</div> :
            texts.map(it => {
              const st = STATUS_LABEL[it.writer_status] || { txt: it.writer_status, color: c.muted };
              const active = sel && sel.id === it.id;
              return (
                <div key={it.id} onClick={() => openItem(it.id)} style={{ padding: "9px 11px", marginBottom: 7, background: active ? (dark ? "#20202b" : "#eef2ff") : c.panel, border: `1px solid ${active ? "#6366f1" : c.border}`, borderRadius: 8, cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: st.color }}>{st.txt}</span>
                    {it.score != null && <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(Number(it.score)) }}>{it.score}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: c.muted, wordBreak: "break-all" }}>{pathOf(it.url)}</div>
                  {it.deadline && <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 3 }}>⏱ {String(it.deadline).slice(0, 10)}</div>}
                </div>
              );
            })}
        </div>

        {/* Editor + rail */}
        {!sel ? (
          <div style={{ flex: 1, textAlign: "center", color: c.muted, padding: "80px 0", fontSize: 14 }}>← Оберіть текст</div>
        ) : (
          <div style={{ flex: 1, display: "flex", gap: 16, alignItems: "flex-start", minWidth: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                <a href={sel.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#6366f1", wordBreak: "break-all" }}>{sel.url} ↗</a>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: c.muted }}>{saving === "saving" ? "Збереження…" : saving === "saved" ? "Збережено ✓" : ""}</span>
                  <button onClick={submit} disabled={submitting} style={{ background: "linear-gradient(135deg,#16a34a,#22c55e)", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700, color: "#fff", cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1 }}>{submitting ? "…" : "Передати на перевірку →"}</button>
                </div>
              </div>

              {sel.writer_status === "revisions" && sel.redactor_comment && (
                <div style={{ background: dark ? "#3a1a1a" : "#fef2f2", border: "1px solid #ef4444", borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#ef4444", marginBottom: 3 }}>⟲ Правки від редактора:</div>
                  <div style={{ fontSize: 13, color: c.text, whiteSpace: "pre-wrap" }}>{sel.redactor_comment}</div>
                </div>
              )}

              <div className="cw-suneditor">
                <SunEditor
                  key={sel.id}
                  setContents={sel.html_content || ""}
                  onChange={onEditorChange}
                  setOptions={{ buttonList: EDITOR_BUTTONS, minHeight: "460px", height: "auto", defaultStyle: "font-size:15px; line-height:1.7;" }}
                  placeholder="Пишіть текст тут…"
                />
              </div>
            </div>

            {/* Right rail: score + ТЗ */}
            <div style={{ width: 300, flexShrink: 0 }}>
              <div style={{ background: c.panel, border: `1px solid ${c.border}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>SEO Score</span>
                  <span style={{ fontSize: 26, fontWeight: 800, color: score ? scoreColor(score.total) : c.muted }}>{score ? score.total : "—"}</span>
                </div>
                {score && score.checks.map((ch, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11, padding: "3px 0", borderTop: i ? `1px solid ${c.border}` : "none" }}>
                    <span style={{ color: ch.status === "ok" ? "#16a34a" : ch.status === "warn" ? "#f59e0b" : "#ef4444", flexShrink: 0 }}>{ch.status === "ok" ? "✓" : ch.status === "warn" ? "◐" : "✕"} {ch.name}</span>
                    <span style={{ color: c.muted, textAlign: "right" }}>{ch.detail}</span>
                  </div>
                ))}
                {!score && <div style={{ fontSize: 12, color: c.muted }}>Почніть писати — оцінка наживо.</div>}
              </div>

              <div style={{ background: c.panel, border: `1px solid ${c.border}`, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 8 }}>Технічне завдання</div>
                <div style={{ fontSize: 12, color: c.muted, marginBottom: 10 }}>Обсяг: <b style={{ color: c.text }}>{recLen.min}–{recLen.max}</b> символів</div>
                {brief.title && <TzRow label="Title" val={brief.title} c={c} />}
                {brief.h1 && <TzRow label="H1" val={brief.h1} c={c} />}
                {brief.description && <TzRow label="Description" val={brief.description} c={c} />}
                {brief.note && <TzRow label="Бриф / побажання" val={brief.note} c={c} />}
                {kwGroups.map((g, gi) => (
                  <div key={gi} style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: c.muted, marginBottom: 4 }}>{g.label} · {g.keys.length}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {g.keys.slice(0, 40).map((k, ki) => <span key={ki} style={{ fontSize: 11, background: dark ? "#22222c" : "#f3f4f6", border: `1px solid ${c.border}`, borderRadius: 5, padding: "2px 6px", color: c.text }}>{k.keyword}</span>)}
                    </div>
                  </div>
                ))}
                {(brief.ngrams || []).length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: c.muted, marginBottom: 4 }}>N-грами · {brief.ngrams.length}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {brief.ngrams.slice(0, 30).map((g, gi) => <span key={gi} style={{ fontSize: 11, background: dark ? "#1c2733" : "#eff6ff", border: `1px solid ${dark ? "#2a3a4a" : "#dbeafe"}`, borderRadius: 5, padding: "2px 6px", color: dark ? "#7dd3fc" : "#0369a1" }}>{g.gram}</span>)}
                    </div>
                  </div>
                )}
                {!kwGroups.length && !(brief.ngrams || []).length && <div style={{ fontSize: 12, color: c.muted }}>ТЗ без семантики.</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TzRow({ label, val, c }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: c.muted }}>{label}</div>
      <div style={{ fontSize: 12, color: c.text, whiteSpace: "pre-wrap" }}>{val}</div>
    </div>
  );
}
